import React, { useEffect, useState, useRef } from "react";
import { inject, observer } from "mobx-react";
import { getFormatedContact, validAusNumberRegExpDeluxe, contactFieldSeperatorRegEx } from "constant/messageConstants";
import FormInput from "components/FormItems/FormInput";
import { Tag, message } from "antd";
import { isEmpty } from "lodash";

const getValidNumber = e => {
  return [
    ...new Set(
      e.flatMap(input =>
        !contactFieldSeperatorRegEx.test(input)
          ? input.match(validAusNumberRegExpDeluxe)
            ? [getFormatedContact(input)]
            : input
                .split(" ")
                .filter(subInput => subInput.match(validAusNumberRegExpDeluxe))
                .map(getFormatedContact)
          : input.split(contactFieldSeperatorRegEx).flatMap(subInput =>
              subInput.trim().match(validAusNumberRegExpDeluxe)
                ? getFormatedContact(subInput.trim())
                : subInput
                    .split(" ")
                    .filter(_ => _.match(validAusNumberRegExpDeluxe))
                    .map(getFormatedContact)
            )
      )
    )
  ];
};

const formatPhoneForDisplay = phone => {
  if (!phone) return phone;

  const cleanNumber = phone.replace(/[^\d+]/g, "");

  if (cleanNumber.startsWith("+61")) {
    return cleanNumber.substring(1);
  } else if (cleanNumber.startsWith("61") && cleanNumber.length >= 11) {
    return cleanNumber;
  } else if (cleanNumber.startsWith("0") && cleanNumber.length === 10) {
    return `61${cleanNumber.substring(1)}`;
  } else if (cleanNumber.length === 9) {
    return `61${cleanNumber}`;
  }

  return phone.replace(/^\+/, "");
};

const TestMessageContactSelector = ({
  size = "default",
  label,
  name,
  placeholder = "Enter mobile numbers",
  errors = {},
  touched = {},
  values = {},
  setFieldValue,
  isTestSms = false,
  containerClassname = "",
  inputClassname = "",
  inputStyle = {},
  filterContactBy = name,
  contactDataField = "numberList",
  onCountChange,
  onContactDataChange,
  secondary
}) => {
  const inputRef = useRef();
  const [value, setValue] = useState("");
  const [tempValues, setTempValues] = useState([]);
  const [validationError, setValidationError] = useState("");

  useEffect(() => {
    if (onCountChange && typeof onCountChange === "function") {
      onCountChange(tempValues.length);
    }
  }, [tempValues.length, onCountChange]);

  useEffect(() => {
    if (onContactDataChange && typeof onContactDataChange === "function") {
      onContactDataChange(tempValues);
    }
  }, [tempValues, onContactDataChange]);

  useEffect(() => {
    if (values[name] && values[name].length > 0) {
      const newTempValues = values[name]
        .map(phoneNumber => {
          if (phoneNumber) {
            const existingContact = (values[contactDataField] || []).find(contact => getFormatedContact(contact.value) === getFormatedContact(phoneNumber));

            const formattedPhone = formatPhoneForDisplay(phoneNumber);

            return {
              label: existingContact?.label || formattedPhone,
              value: formattedPhone,
              number: formattedPhone,
              contactData: existingContact?.contactData || {
                first_name: "",
                last_name: ""
              }
            };
          }
        })
        .filter(Boolean);
      setTempValues(newTempValues);
    } else {
      setTempValues([]);
    }
  }, [values[name]]);

  const handleAddContact = () => {
    if (isEmpty(value)) return;

    const inputArray = [value];
    const validContacts = getValidNumber(inputArray);

    if (validContacts.length === 0) {
      setValidationError("Invalid phone number format");
      return;
    }

    setValidationError("");
    const newContacts = validContacts.filter(contact => !values[name]?.includes(contact));

    if (newContacts.length > 0) {
      const updatedValues = [...(values[name] || []), ...newContacts];
      setFieldValue(name, updatedValues);

      const newTempItems = newContacts.map(contactValue => ({
        label: contactValue,
        value: contactValue,
        number: contactValue,
        contactData: {}
      }));

      setTempValues([...tempValues, ...newTempItems]);
      setFieldValue(contactDataField, [...(values[contactDataField] || []), ...newTempItems]);
    }

    setValue("");
  };

  const onPressEnter = event => {
    event.stopPropagation();
    event.preventDefault();
    handleAddContact();
    return false;
  };

  const handleRemoveContact = itemValue => {
    setFieldValue(
      name,
      values[name].filter(i => i !== itemValue)
    );
    const newTempValues = tempValues.filter(i => i.value !== itemValue);
    setTempValues(newTempValues);
  };

  const handleInputChange = e => {
    setValue(e.target.value);
    if (validationError) {
      setValidationError("");
    }
  };

  const handleInputChangeWithValidation = e => {
    const inputValue = e.target.value;
    setValue(inputValue);

    if (validationError) {
      setValidationError("");
    }

    if (inputValue && !inputValue.match(validAusNumberRegExpDeluxe) && inputValue.length > 5) {
      const validNumbers = getValidNumber([inputValue]);
      if (validNumbers.length === 0) {
        setValidationError("Invalid phone number format");
      }
    }
  };

  return (
    <div>
      <div className="d-flex gap-2 align-items-end">
        <div className="flex-grow-1">
          <FormInput
            containerClassname={`mb-2 ${containerClassname}`}
            inputClassname={inputClassname}
            size={size}
            label={label}
            name={name}
            style={inputStyle}
            placeholder={placeholder}
            error={errors[name] || validationError}
            touched={touched[name] || !!validationError}
            value={value}
            onChange={handleInputChangeWithValidation}
            inputProps={{
              "data-cy": "contact-selector-input",
              ref: inputRef,
              autoComplete: "off",
              onPressEnter,
              onBlur: handleAddContact
            }}
            secondary={secondary}
          />
        </div>
      </div>

      <div className="d-flex gap-2 align-items-center flex-wrap mb-3">
        {tempValues.map(item => {
          if (item) {
            return (
              <Tag key={item?.value} closable onClose={() => handleRemoveContact(item.value)}>
                {item.label}
              </Tag>
            );
          }
        })}
      </div>
    </div>
  );
};

export default inject(stores => ({
  contactStore: stores.store.contactStore,
  modalStore: stores.store.modalStore
}))(observer(TestMessageContactSelector));
